import Table, { buildTwoLineColumn, buildExpandableCountColumn, buildTagColumn } from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import EmptyState from "@/components/EmptyState";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import type { EducationCertification } from "@/types";
import { formatCertificationProgramData, type LifelongLearningProgressRow } from "./utils";
import type { ProgramDetailRow } from "../PGMProgramTable/utils";

export interface LifelongLearningTableProps {
  certificationHistory?: EducationCertification[];
  loading?: boolean;
  testId?: string;
}

export default function LifelongLearningTable({
  certificationHistory = [],
  loading = false,
  testId = "learning-history-table"
}: LifelongLearningTableProps) {
  const tableData = formatCertificationProgramData(certificationHistory);
  const levelColumn = buildTwoLineColumn<LifelongLearningProgressRow>({
    accessorKey: "level",
    header: "Level",
    getPrimaryText: (row) => row.programDescription,
    getSecondaryText: (row) => (row.code !== row.programName ? row.code : ""),
    size: 300,
    isFirstColumn: true
  });
  const columns = [
    {
      ...levelColumn,
      meta: {
        ...levelColumn.meta,
        className: getStickyColumnClasses(tableData.length > 0)
      }
    },
    {
      accessorKey: "version",
      header: "Version",
      enableSorting: false
    },
    {
      accessorKey: "careerPath",
      header: "Career Path",
      enableSorting: false
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      enableSorting: false
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      enableSorting: false
    },
    {
      accessorKey: "expiresDate",
      header: "Expiration",
      enableSorting: false,
      cell: ({ row }: { row: { original: LifelongLearningProgressRow } }) => (
        <span className={row.original.isExpired ? "text-sys-brand-error" : ""}>{row.original.expiresDate || "--"}</span>
      )
    },
    buildExpandableCountColumn<LifelongLearningProgressRow>({
      accessorKey: "requirements",
      header: "Requirements",
      getCount: (row) => row.completedDetailsCount,
      getTotal: (row) => row.detailsCount
    }),
    buildTagColumn<LifelongLearningProgressRow>({
      accessorKey: "status",
      header: "Level Status",
      getTagLabel: (row) => row.status,
      getTagType: (row) => (row.status === "Complete" ? "success" : "warn")
    })
  ];

  const detailsColumns = [
    buildTwoLineColumn<ProgramDetailRow>({
      accessorKey: "description",
      header: "Description",
      getPrimaryText: (row) => row.description,
      getSecondaryText: (row) => row.secondaryText,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "requireTypeLabel",
      header: "Type",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.requireTypeLabel || "--"}</span>
      ),
      enableSorting: false,
      size: 120
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.date || "--"}</span>
      ),
      enableSorting: false,
      size: 150
    },
    {
      accessorKey: "scoreText",
      header: "Score",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) =>
        detailRow.original.scoreText ? (
          <span>{detailRow.original.scoreText}</span>
        ) : (
          <span className="opacity-20">--</span>
        ),
      enableSorting: false,
      size: 100
    },
    buildTagColumn<ProgramDetailRow>({
      accessorKey: "statusLabel",
      header: "Status",
      getTagLabel: (row) => row.statusLabel,
      getTagType: (row) => row.statusType,
      size: 120
    })
  ];

  return (
    <Table
      data={tableData}
      columns={columns}
      loading={loading}
      data-testid={testId}
      getRowCanExpand={(row) => row.original.detailsCount > 0}
      renderExpandedRow={({ row }) => (
        <TableDetailsPanel
          data={row.original.details}
          columns={detailsColumns}
          title={`Requirements (${row.original.completedDetailsCount}/${row.original.detailsCount})`}
        />
      )}
      renderNoDataEmptyState={() => (
        <EmptyState
          title="No Programs"
          caption="This person has not started any lifelong learning programs"
          iconName="workspace_premium"
        />
      )}
    />
  );
}
